<template>
  <div class="container">
    <div class="row">
      <div class="col-12">
        <search-bar></search-bar>
        <div class="clearfix">
          <b-button
            class="btn btn-success float-left"
            :href="$routes('program.messages.create')"
            v-text="$t('buttons.create')"
            :disabled="
              states.searching ||
              !(permissions.indexOf('message-create') !== -1)
            "
          />

          <div class="float-right">
            <common-paginator />
          </div>
        </div>

        <common-loading
          :message="$t('messages.deleting')"
          v-if="states.deleting"
          class="my-2"
        />
        <b-alert
          variant="danger"
          :show="errorMessage != null"
          class="mt-2 text-center"
        >
          <em class="fa fa-times-circle mr-2"></em>
          {{ errorMessage }}
        </b-alert>

        <b-alert
          variant="success"
          :show="successMessage != null"
          class="mt-2 text-center"
        >
          <em class="fa fa-check-circle mr-2"></em>
          {{ successMessage }}
        </b-alert>

        <b-table
          striped
          hover
          bordered
          show-empty
          fixed
          thead-class="table-primary"
          :items="items"
          :fields="fields"
          :busy="states.searching"
          class="mt-2"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>{{ $t("commons.loading") }}</strong>
            </div>
          </template>

          <template v-slot:cell(actions)="row">
            <b-button
              variant="info"
              v-if="permissions.indexOf('message-edit') === -1"
              :disabled="
                states.deleting || !(permissions.indexOf('message-read') !== -1)
              "
              class="m-1"
              @click="editMessage(row.item.id)"
            >
              <i class="fa fa-eye"></i>
            </b-button>
            <b-button
              variant="primary"
              v-if="permissions.indexOf('message-edit') !== -1"
              :disabled="
                states.deleting ||
                !(
                  permissions.indexOf('message-edit') !== -1 &&
                  permissions.indexOf('message-read') !== -1
                )
              "
              @click="editMessage(row.item.id)"
              class="m-1"
            >
              <i class="fa-solid fa-pen-to-square"></i>
            </b-button>
            <b-button
              variant="danger"
              v-if="permissions.indexOf('message-delete') !== -1"
              :disabled="
                states.deleting ||
                !(permissions.indexOf('message-delete') !== -1)
              "
              @click="deleteMessage(row.item.id)"
              class="m-1"
            >
              <i class="fa fa-trash"></i>
            </b-button>
          </template>
        </b-table>

        <confirmation-modal ref="confirmModal"></confirmation-modal>
      </div>
    </div>
  </div>
</template>

<script>
import MessageApi from "../../../apis/MessagesApi";
import { useSearch } from "../../../stores/search";
import { useManageAttributes } from "../../../stores/manage-attributes";

export default {
  props: {
    permissions: Object | Array,
  },
  created() {
    // Trigger Search.
    this.useSearch.updateStates({
      searching: true,
    });

    this.useManageAttributes.setPermissions(this.permissions);
  },
  data() {
    return {
      instanceId: window.Laravel.selected_instance
        ? window.Laravel.selected_instance.id
        : null,
      fields: [
        {
          key: "id",
          label: this.$t("commons.id"),
        },
        "name",
        "description",
        {
          key: "status",
          label: this.$t("commons.status"),
        },
        {
          key: "createTime",
          label: this.$t("commons.created_time"),
          formatter: (value) => {
            return this.$options.filters.datetime(value);
          },
        },
        {
          key: "lastModifiedTime",
          label: this.$t("commons.last_modified"),
          formatter: (value) => {
            return this.$options.filters.datetime(value);
          },
        },
        {
          key: "actions",
          label: "Actions",
          thStyle: { width: "17%" },
        },
      ],
      errorMessage: null,
      successMessage: null,
      useSearch: useSearch(),
      useManageAttributes: useManageAttributes()
    };
  },
  computed: {
    searchMode() {
      return this.useSearch.getSearchMode();
    },
    pagination() {
      return this.useSearch.pagination;
    },
    items() {
      return this.useSearch.items;
    },
    states() {
      return this.useSearch.states;
    },
  },
  watch: {
    "states.searching": function (newVal, oldVal) {
      if (newVal) {
        this.getMessages(this.useSearch.searchCriteria);
      }
    },
  },
  methods: {
    getMessages(filters) {
      this.errorMessage = this.successMessage = null;
      this.useSearch.hasError = false;

      filters += this.useSearch.getActiveFilter;
      let params = {
        filters: filters,
        limit: this.pagination.limit,
        offset: (this.pagination.page - 1) * this.pagination.limit,
      };

      MessageApi.all(params, this.instanceId)
        .then((response) => {
          this.useSearch.setItems(response.result);
          this.useSearch.updateStates({
            searching: false,
          });
        })
        .catch((e) => {
          this.useSearch.updateStates({
            searching: false,
            hasError: true,
          });
          this.errorMessage = this.$t("messages.search_failed");
          if (e.response && e.response.status == 400) {
            this.errorMessage = e.response.data.data.detail;
          }
          this.$toast.error("Error", this.errorMessage);
        });
    },
    editMessage(id) {
      window.location.href = this.$routes("program.messages.edit", {
        id: id,
      });
    },
    deleteMessage(id) {
      this.$refs.confirmModal
        .open(this.$t("messages.delete_message"))
        .then((response) => {
          if (response) {
            this.useSearch.updateStates({
              deleting: true,
              showSuccess: false,
            });
            this.errorMessage = this.successMessage = null;
            MessageApi.delete(this.instanceId, id)
              .then(() => {
                this.useSearch.updateStates({
                  searching: true,
                  deleting: false,
                  showSuccess: true,
                });
                this.successMessage = this.$t("messages.deleted_successfully");
                this.$toast.success("Success", this.successMessage);
              })
              .catch((e) => {
                this.useSearch.updateStates({
                  deleting: false,
                });
                if (e.response.status == 423) {
                  this.errorMessage = this.$t("messages.record_in_use");
                  this.$toast.error("Error", this.errorMessage);
                  this.useManageAttributes.setErrorMessage(this.errorMessage);
                } else if (e.response.status == 404) {
                  this.errorMessage = this.$t("messages.already_deleted");
                  this.$toast.error("Error", this.errorMessage);
                  this.useManageAttributes.setErrorMessage(this.errorMessage);
                } else {
                  this.errorMessage = this.$t("messages.unable_to_delete");
                  this.$toast.error("Error", this.errorMessage);
                }
              });
          }
        });
    },
  },
};
</script>
